import type { FetchResponse, RestService, RequestParams, Interceptor } from '@sydle/ui-api';
import { ProcessMethods } from '../process-api';
import type { SelectUserArgs} from './delegation-interceptor-utils';
import { hasDefinedDelegation, hasPendingDelegation, selectUser } from './delegation-interceptor-utils';
import type { URLInfosType} from '../utils/url';
import { extractInfosByURL } from '../utils/url';

export class ProcessDelegationInterceptor implements Interceptor {
  private delegationSelected = {};

  interceptorRequest = async (params: Partial<RequestParams>, isCompletedURL?: boolean): Promise<any> => {
    const requestInfos = extractInfosByURL(params.url, isCompletedURL);
    if (!requestInfos.methodIdentifier || !this.isProcessRequest(requestInfos.methodIdentifier)) return params;
    if (hasDefinedDelegation(params)) return params;
    const _checkDelegation = true;
    const _delegationUser = this.verifyDelegationUserSelected(requestInfos, params);
    if (params.method === 'GET') {
      return params;
    }

    return {
      ...params,
      method: 'POST',
      body: { ...params.body, _checkDelegation, _delegationUser },
    };
  };

  interceptorResponse = async (
    response: FetchResponse,
    requestParams: RequestParams,
    restService: RestService,
  ): Promise<any> => {
    const requestInfos = extractInfosByURL(response.meta.request.url, true);

    if (
      !requestInfos.methodIdentifier ||
      (!this.isAttendanceDraftRequest(requestInfos.methodIdentifier) &&
        !this.isProcessRequest(requestInfos.methodIdentifier))
    )
      return response;
    if (this.isAttendanceDraftRequest(requestInfos.methodIdentifier) || requestParams.body._delegationUser) {
      this.fillDelegationArray(requestInfos, requestParams.body._delegationUser, response);
      return response;
    }
    if (!hasPendingDelegation(response)) return response;

    const userOptions: SelectUserArgs[] = response.error.data._userDelegation;

    const delegationUser = await selectUser(userOptions);
    if (!delegationUser) {
      return response;
    }
    requestParams.body._delegationUser = delegationUser.reference;

    this.fillDelegationArray(requestInfos, delegationUser.reference, response, true);

    requestParams.addBaseUrl = false;

    const result = await restService.fetch(requestParams);
    return result;
  };

  private isProcessRequest(methodIdentifier: string): boolean {
    return (
      this.isStartRequest(methodIdentifier) ||
      this.isAttendRequest(methodIdentifier) ||
      this.isTransferRequest(methodIdentifier) ||
      this.isCreateIntervationRequest(methodIdentifier) ||
      this.isDeleteRequest(methodIdentifier) ||
      this.isReturnTaskRequest(methodIdentifier)
    );
  }

  private isStartRequest(methodIdentifier: string): boolean {
    return methodIdentifier === ProcessMethods.START;
  }

  private isCreateIntervationRequest(methodIdentifier: string): boolean {
    return methodIdentifier === ProcessMethods.CREATE_INTERVATION;
  }

  private isAttendRequest(methodIdentifier: string): boolean {
    return methodIdentifier && methodIdentifier.indexOf(ProcessMethods.ATTEND) !== -1;
  }

  private isAttendanceDraftRequest(methodIdentifier: string): boolean {
    return methodIdentifier === ProcessMethods.ATTENDANCE_DRAFT;
  }

  private isTransferRequest(methodIdentifier: string): boolean {
    return methodIdentifier?.indexOf(ProcessMethods.TRANSFER) !== -1;
  }

  private isDeleteRequest(methodIdentifier: string): boolean {
    return methodIdentifier && methodIdentifier.indexOf(ProcessMethods.DELETE) !== -1;
  }

  private isReturnTaskRequest(methodIdentifier: string): boolean {
    return methodIdentifier && methodIdentifier.indexOf(ProcessMethods.RETURN_TASK) !== -1;
  }

  private verifyDelegationUserSelected(requestInfos: URLInfosType, params?: Partial<RequestParams>): string {
    if (
      this.isAttendanceDraftRequest(requestInfos.methodIdentifier) ||
      this.isTransferRequest(requestInfos.methodIdentifier)
    ) {
      return this.delegationSelected?.[params.body.userTaskId];
    }
    if (this.isAttendRequest(requestInfos.methodIdentifier)) {
      return this.delegationSelected?.[requestInfos.classId];
    }

    if (
      this.isDeleteRequest(requestInfos.methodIdentifier) ||
      this.isReturnTaskRequest(requestInfos.methodIdentifier)
    ) {
      return this.delegationSelected?.[params.body.elementId];
    }

    return null;
  }

  private fillDelegationArray(
    requestInfos: URLInfosType,
    delegationUser: any,
    response: FetchResponse,
    ignoreAttendRequest?: boolean,
  ): void {
    if (ignoreAttendRequest) return;

    if (this.isAttendanceDraftRequest(requestInfos.methodIdentifier)) {
      return;
    }

    const isAttendRequest = this.isAttendRequest(requestInfos.methodIdentifier);

    if (isAttendRequest) {
      this.delegationSelected[response.data._publishedObject?._id] = delegationUser;
      return;
    }
  }
}
